import React from 'react';

type ProgressEvent = {
  type?: 'progress';
  status?: 'start' | 'done' | 'error';
  state?: 'CHAT' | 'PLAN' | 'EXECUTION' | string;
  tool?: string;
  stage?: string;
  detail?: string;
};

function StateIcon({ state }: { state?: string }) {
  const s = (state || '').toUpperCase();

  // Flat, Cursor-like icon (no container chip). Using slightly larger size for clarity.
  const iconClass = 'h-5 w-5 text-[#94a3b8] flex-shrink-0';

  // PLAN: Cursor-style outline/list (bullet + line rows)
  if (s === 'PLAN') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-label="Planning">
        {/* Row 1 */}
        <circle cx="5" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="6" x2="20" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        {/* Row 2 */}
        <circle cx="5" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        {/* Row 3 */}
        <circle cx="5" cy="18" r="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="10" y1="18" x2="20" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  // EXECUTION: Workflow/tools icon (tool-call style)
  if (s === 'EXECUTION') {
    return (
      <svg viewBox="0 0 32 32" fill="none" className={iconClass} aria-label="Building">
        <path
          d="M25,21c-.7396,0-1.4241,.2155-2.0191,.5669l-2.0314-2.0314-1.4141,1.4141,2.0314,2.0314c-.3514,.595-.5668,1.2795-.5668,2.019,0,2.2056,1.7944,4,4,4,.3557,0,.6943-.0615,1.0228-.1492l-2.4368-2.4368,.0004-.0005c-.3621-.3621-.5864-.8621-.5864-1.4136,0-1.103,.897-2,2-2,.5515,0,1.0515,.2242,1.4136,.5864l.0004-.0005,2.4368,2.4368c.0875-.3284,.1491-.667,.1491-1.0227,0-2.2056-1.7944-4-4-4Z"
          fill="currentColor"
        />
        <path
          d="M20.9495,12.4644l3.7645-3.7645c.3911,.1868,.8237,.3,1.2861,.3,1.6569,0,3-1.3431,3-3s-1.3431-3-3-3-3,1.3431-3,3c0,.4622,.1132,.8948,.2999,1.2859l-3.7645,3.7645,1.4141,1.4141Zm5.0505-7.4644c.5514,0,1,.4486,1,1s-.4486,1-1,1-1-.4486-1-1,.4486-1,1-1Z"
          fill="currentColor"
        />
        <path
          d="M16,12c-2.2092,0-4,1.7908-4,4,0,.7405,.215,1.4254,.5657,2.0201l-5.2795,5.2799c-.3911-.1868-.8238-.3-1.2861-.3-1.6569,0-3,1.3431-3,3s1.3431,3,3,3,3-1.3431,3-3c0-.4622-.1132-.8948-.2999-1.2858l5.2799-5.2799c.5948,.3507,1.2795,.5657,2.02,.5657,2.2091,0,4-1.7909,4-4s-1.7909-4-4-4ZM6,27c-.5514,0-1-.4486-1-1s.4486-1,1-1,1,.4486,1,1-.4486,1-1,1Zm10-9c-1.1028,0-2-.8972-2-2s.8972-2,2-2,2,.8972,2,2-.8972,2-2,2Z"
          fill="currentColor"
        />
        <path
          d="M7,11c.7396,0,1.4241-.2155,2.0191-.5669l2.0311,2.0311,1.4141-1.4141-2.0311-2.0311c.3514-.595,.5668-1.2795,.5668-2.019,0-2.2056-1.7944-4-4-4-.3557,0-.6943,.0615-1.0228,.1492l2.4368,2.4368-.0004,.0005c.3621,.3621,.5864,.8621,.5864,1.4136,0,1.103-.897,2-2,2-.5515,0-1.0515-.2242-1.4136-.5864l-.0004,.0005-2.4368-2.4368c-.0875,.3284-.1491,.667-.1491,1.0227,0,2.2056,1.7944,4,4,4Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  // Default: CHAT (chat bubble with tail)
  return (
    <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-label="Chatting">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AgentProgressTicker({
  event,
}: {
  event?: ProgressEvent | null;
}) {
  /**
   * UX goal:
   * - Always show "typing" dots (so users clearly see the agent is still working).
   * - Keep the main stage fixed (so we don't hide the most important info).
   * - Show a secondary line ONLY when detail exists (no generic rotating text).
   */
  const stage = (event?.stage || '').trim() || 'Working…';
  const detail = (event?.detail || '').trim();

  return (
    <div
      className="mt-4 flex items-center gap-3"
      aria-live="polite"
      aria-label="Progress"
    >
      {/* Left: state icon (deterministic, non-technical). */}
      <StateIcon state={event?.state} />

      {/* Middle: message(s). */}
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="text-[14px] leading-5 text-[#e5e7eb] truncate">
          {stage}
        </div>
        {detail ? (
          <div className="text-[12px] leading-4 text-[#9ca3af] truncate">
            {detail}
          </div>
        ) : null}
      </div>

      {/* Right: typing dots (blue, WhatsApp-style slower bounce). */}
      <div className="flex items-center gap-1" aria-hidden="true">
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '0ms',
          }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '200ms',
          }}
        />
        <span
          className="h-2 w-2 rounded-full bg-[#3b82f6]"
          style={{
            animation: 'typing-bounce 1.4s ease-in-out infinite',
            animationDelay: '400ms',
          }}
        />
      </div>
      {/* Keyframes: slower, smoother bounce like WhatsApp */}
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
}

